/**
 * Tooltip Component
 *
 * A lightweight, accessible tooltip for inline help and hints.
 * - Desktop: Hover-only (appears on hover, disappears when not hovering)
 * - Mobile: Completely disabled (no tooltips shown)
 */

import React, { useState, useRef, useEffect } from "react";
import styles from "../styles/Tooltip.module.css";

interface TooltipProps {
    content: React.ReactNode;
    children: React.ReactNode;
    position?: "top" | "bottom" | "left" | "right";
}

export default function Tooltip({
    content,
    children,
    position = "top",
}: TooltipProps) {
    const [isVisible, setIsVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsMobile("ontouchstart" in window || navigator.maxTouchPoints > 0);
    }, []);

    const handleMouseEnter = () => {
        // Only show tooltip on desktop, never on mobile
        if (!isMobile) {
            setIsVisible(true);
        }
    };

    const handleMouseLeave = () => {
        // Hide tooltip when mouse leaves (hover-only behavior)
        setIsVisible(false);
    };

    // On mobile, don't show tooltips at all - return children without tooltip
    if (isMobile) {
        return <>{children}</>;
    }

    return (
        <div
            ref={tooltipRef}
            className={styles.tooltipWrapper}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {isVisible && content && (
                <div className={`${styles.tooltip} ${styles[position]}`}>
                    <div className={styles.tooltipContent}>{content}</div>
                    <div className={styles.tooltipArrow} />
                </div>
            )}
        </div>
    );
}
